// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useEffect, useRef, useState, type ReactNode } from 'react';

import styles from './Tile.module.css';

export type TileSlot = 'left' | 'right' | 'bottom' | 'centre';

export interface TileProps {
  slot: TileSlot;
  /** left: the wide variant. */
  wide?: boolean;
  /** left: on a phone, one sideways-scrolling bar under the top bar instead of hidden. */
  phoneBar?: boolean;
  /** right: runs to the bottom gutter. centre: the whole area under the top bar. */
  full?: boolean;
  /** left/right/centre: no strip under it, so it runs to (or may reach) the bottom gutter. */
  stripless?: boolean;
  /** centre: no inspector beside it, so it runs to the right gutter. */
  solo?: boolean;
  /** centre: a left tile stays beside it (`ModeView.leftWithCentre`), so it starts right of that one. */
  besideLeft?: 'narrow' | 'wide' | null;
  /**
   * centre, on a phone: the left tile is a bar there (`phoneBar`), so it
   * starts under the bar, and stops above the right sheet even without a strip.
   */
  underBar?: boolean;
  /**
   * left/right: no surface of its own — the children are separate `Panel`s
   * (`ModeView.stacked`). Use it for any titled content: the Panel's header
   * stays fixed while its body scrolls.
   */
  stacked?: boolean;
  /** Changing it replays the swap-in (a new selection in the same slot). */
  swapKey?: string | number | null;
  /** The landmark's accessible name. */
  label?: string;
  /** `aria-live="polite"` for an inspector whose content follows the selection. */
  live?: boolean;
  children: ReactNode;
}

const ELEMENT: Record<TileSlot, 'div' | 'aside' | 'nav' | 'section'> = {
  left: 'div',
  right: 'aside',
  bottom: 'nav',
  centre: 'section',
};

/**
 * One floating panel over the Ground: translucent over a backdrop blur,
 * placed by slot with the layout tokens. It fades/slides in when mounted and
 * again whenever `swapKey` changes (the v8 swap; none with reduced motion).
 * On phones (≤ 900px) the left slot is hidden (or, with `phoneBar`, a bar
 * under the top bar), the right becomes a sheet above the strip, and the
 * centre sits above that.
 */
export function Tile({ slot, wide, phoneBar, full, stripless, solo, besideLeft, underBar, stacked, swapKey, label, live, children }: TileProps) {
  // Alternating between two identical keyframes restarts the animation
  // without remounting the content (scroll and focus survive).
  const [phase, setPhase] = useState(false);
  const firstKey = useRef(swapKey);
  useEffect(() => {
    if (firstKey.current === swapKey) return;
    firstKey.current = swapKey;
    setPhase((p) => !p);
  }, [swapKey]);

  const Element = ELEMENT[slot];
  const cls = [
    styles.tile,
    styles[slot],
    wide && slot === 'left' ? styles.wide : '',
    phoneBar && slot === 'left' ? styles.phoneBar : '',
    full ? styles.full : '',
    stripless ? styles.stripless : '',
    solo ? styles.solo : '',
    besideLeft && slot === 'centre' ? (besideLeft === 'wide' ? styles.besideWide : styles.besideNarrow) : '',
    underBar && slot === 'centre' ? styles.underBar : '',
    stacked && (slot === 'left' || slot === 'right') ? styles.stacked : '',
    phase ? styles.swapB : styles.swapA,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <Element className={cls} aria-label={label} aria-live={live ? 'polite' : undefined}>
      {children}
    </Element>
  );
}
